import React, { type FC, useMemo } from "react";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import moment, { type Moment } from "moment-timezone";
import { useTranslation } from "react-i18next";
import { useAuth } from "components/Auth";
import { LabelValue } from "components/Shared/LabelValue";
import { toShortTimezone } from "utils";
import useTimezoneUtils from "hooks/useTimezoneUtils";
import clsx from "clsx";

import "./DateRangeWidget.scss";

interface DateRangeWidgetProps {
    startDate: Moment | Date | string;
    endDate: Moment | Date | string;
    clientTimezone?: string;
    inline?: boolean;
}

const DateRangeBlock: FC<{ startDate: Moment, endDate: Moment }> = ({ startDate, endDate }) => {
    const { t } = useTranslation('criticalIncident', { useSuspense: false });
    const isSameDay = useMemo(() => startDate.isSame(endDate, 'day'), [startDate, endDate]);
    const { localeTimeFormat } = useTimezoneUtils();

    return (
        <div className="date-range-widget">
            <div className="date-block start">
                <LabelValue
                    value={(
                            <div className="date-value">
                                <strong>{startDate.format('MMM Do')}</strong>
                                <span>{startDate.format(localeTimeFormat)}</span>
                            </div>
                        )}
                    label={!isSameDay ? <div className="secondary-text">{t('start_date')}</div> : ''}
                />
            </div>
            <div className="vertical-divider"><div className="icon">→</div></div>
            <div className="date-block end">
                <LabelValue
                    value={(
                        <div className="date-value">
                            <strong>{isSameDay ? '\u00A0' : endDate.format('MMM Do')}</strong>
                            <span>{endDate.format(localeTimeFormat)}</span>
                        </div>
                    )}
                    label={!isSameDay ? <div className="secondary-text">{t('end_date')}</div> : ''}
                />
            </div>
        </div>
    );
}

const DateRangeLine: FC<{ startDate: Moment, endDate: Moment }> = ({ startDate, endDate }) => {
    const { localeTimeFormat } = useTimezoneUtils();

    return (
        <div className="date-range-widget">
            <div className="date-block start">
                <div className="date-value">
                    <span>{startDate.format('MMM Do')}, </span>
                    <span>{startDate.format(localeTimeFormat)}</span>
                </div>
            </div>
            <div className="icon">→</div>
            <div className="date-block end">
                <div className="date-value">
                    <span>{endDate.format(localeTimeFormat)}</span>
                </div>
            </div>
        </div>
    );
};

export const DateRangeWidget: FC<DateRangeWidgetProps> = React.memo(({ startDate, endDate, clientTimezone, inline }) => {
    const { effectiveProviderData } = useAuth();
    const providerTimezone = effectiveProviderData?.timeZone;
    const { t, i18n } = useTranslation('criticalIncident', { useSuspense: false });
    moment.locale(i18n.language);
    const providerShortTimeZone = providerTimezone ? toShortTimezone(providerTimezone, moment(startDate).toDate()) : '';
    const clientShortTimeZone = clientTimezone ? toShortTimezone(clientTimezone, moment(startDate).tz(clientTimezone).toDate()) : '';
    const date1 = moment.isMoment(startDate) ? startDate : moment(startDate);
    const date2 = moment.isMoment(endDate) ? endDate : moment(endDate);
    const isSameDay = useMemo(() => date1.isSame(date2, 'day'), [date1, date2]);
    const isSameTimezone = !clientTimezone || (clientShortTimeZone === providerShortTimeZone);

    const sameTimezoneDateRange = useMemo(() => {
        return (
            <div className={clsx({'date-range-widget-wrapper': true, 'same-day' : isSameDay, 'inline': inline })}>
                <LabelValue
                    value={(
                        inline ? <DateRangeLine startDate={date1} endDate={date2} /> : <DateRangeBlock startDate={date1} endDate={date2} />
                    )}
                    label={
                        isSameDay ? (
                            <span>
                                {t('date_and_time')} ({providerShortTimeZone}) <sup className="timezone-hint" data-tooltip={t('time_zone_hint')}><InfoIcon color="primary" /></sup>
                            </span>
                        ) : ''
                    }
                />
            </div>
        );
    }, [date1, date2, isSameDay, t, providerShortTimeZone, i18n.language]);

    const crossTimezoneDateRange = useMemo(() => {
        return (
            <div className="date-range-widget-wrapper">
                <LabelValue
                    label={t('date_and_time')}
                    value={(
                        <div className="cross-timezone-date-range">
                            <LabelValue
                                value={<DateRangeLine startDate={date1} endDate={date2} />}
                                label={(
                                    <span>
                                        {t('your_time')} ({providerShortTimeZone}) <sup className="timezone-hint" data-tooltip={t('time_zone_hint')}><InfoIcon color="primary" /></sup>
                                    </span>
                                )}
                            />
                            <LabelValue
                                value={<DateRangeLine startDate={moment.tz(date1, clientTimezone || '')} endDate={moment.tz(date2, clientTimezone || '')} />}
                                label={(
                                    <span>
                                        {t('client_time')} ({clientShortTimeZone})
                                    </span>
                                )}
                            />
                        </div>
                    )}
                />
            </div>
        );
    }, [date1, date2, isSameDay, t, providerShortTimeZone, clientShortTimeZone, i18n.language]);

    return isSameTimezone ? sameTimezoneDateRange : crossTimezoneDateRange;
});
